import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import Tooltipable from "~/components/effects/tooltipable";
import CONTEXT_DEPTHS, { DEPTH_STORAGE_KEY } from "~/constants/ContextDepths";

const ContextDepthSelector = ({
  selectedDepth,
  onDepthChange,
  disabled,
  loading,
  isTemplateSet = false,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const selected =
    CONTEXT_DEPTHS.find((d) => d.value === selectedDepth) || CONTEXT_DEPTHS[0];

  const tooltipContent = (
    <div className="text-left" style={{ maxWidth: 280 }}>
      <strong>Context Depth</strong>
      <hr className="my-1 border-light" />
      {CONTEXT_DEPTHS.map((depth, idx) => (
        <div key={depth.value} className={idx > 0 ? "mt-2" : undefined}>
          <div>
            <FontAwesomeIcon icon={["far", depth.icon]} className="mr-1" />
            <strong>{depth.label}</strong>
            {depth.value === selected.value && (
              <span className="ml-1 text-success">(current)</span>
            )}
          </div>
          <div className="small">{depth.description}</div>
        </div>
      ))}
    </div>
  );

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen]);

  const handleSelect = (depth) => {
    if (depth.value !== selectedDepth) {
      // BK-3229 R4 AC 3.1.3 — skip localStorage while template-driven.
      if (!isTemplateSet) {
        localStorage.setItem(DEPTH_STORAGE_KEY, depth.value);
      }
      onDepthChange(depth.value);
    }
    setIsOpen(false);
  };

  return (
    <div
      ref={dropdownRef}
      className="d-flex align-items-center flex-nowrap position-relative"
    >
      <button
        type="button"
        className="btn btn-sm btn-outline-success rounded-pill text-nowrap text-sm py-0 px-2"
        onClick={() => setIsOpen((prev) => !prev)}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        {loading ? (
          <FontAwesomeIcon icon="far fa-spinner" pulse className="mr-1" />
        ) : (
          <FontAwesomeIcon icon={["far", selected.icon]} className="mr-1" />
        )}
        <span>Context Depth</span>
        <FontAwesomeIcon
          icon={["fas", "fa-caret-down"]}
          className="ml-1 text-xs"
        />
      </button>

      <Tooltipable trigger="click" placement="right" text={tooltipContent}>
        <FontAwesomeIcon
          icon={["fal", "fa-info-circle"]}
          className="ml-2 text-secondary text-xs cursor-pointer"
        />
      </Tooltipable>

      {isOpen && (
        <div
          role="listbox"
          aria-label="Context depth selection"
          className="dropdown-menu show shadow-sm"
          style={{ minWidth: "220px" }}
        >
          {CONTEXT_DEPTHS.map((depth) => (
            // `<button>` gives us native Enter/Space activation + focus ring
            // without the bespoke keydown handler. `role="option"` preserves
            // the listbox/option semantics the wrapper announces. Bootstrap's
            // `.dropdown-item` already sets width/border/bg and drives the
            // hover/active styling — no extra helpers needed.
            <button
              key={depth.value}
              type="button"
              role="option"
              aria-selected={depth.value === selectedDepth}
              className={`dropdown-item px-3 py-2 ${depth.value === selectedDepth ? "bg-light" : ""}`}
              onClick={() => handleSelect(depth)}
            >
              <div className="d-flex align-items-center">
                {depth.value === selectedDepth ? (
                  <FontAwesomeIcon
                    icon="far fa-check"
                    className="mr-2 text-success fa-fw"
                  />
                ) : (
                  <span className="mr-2 fa-fw d-inline-block" />
                )}
                <FontAwesomeIcon icon={["far", depth.icon]} className="mr-1" />
                {depth.label}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

ContextDepthSelector.propTypes = {
  selectedDepth: PropTypes.string.isRequired,
  onDepthChange: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  isTemplateSet: PropTypes.bool,
};

export default ContextDepthSelector;
